'use client'

import { useState, useRef } from 'react'
import { useRouter } from 'next/navigation'

export default function ReputationBadge({ 
  targetUserId, 
  initialRep, 
  currentUserId,
  initialUserVote = null
}: { 
  targetUserId: string, 
  initialRep: number, 
  currentUserId: string | null,
  initialUserVote?: number | null
}) {
  const [rep, setRep] = useState(initialRep)
  const [userVote, setUserVote] = useState<number | null>(initialUserVote)
  const [loading, setLoading] = useState(false)
  
  const isProcessing = useRef(false)
  const router = useRouter()

  const handleRep = async (value: number) => {
    if (!currentUserId) {
      router.push('/login')
      return
    }
    if (currentUserId === targetUserId) return
    if (isProcessing.current) return
    
    isProcessing.current = true
    setLoading(true)

    try {
      const res = await fetch(`/api/users/${targetUserId}/rep`, { 
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value })
      })
      if (res.ok) {
        const data = await res.json()
        setRep(data.reputation)
        setUserVote(data.userVote)
      }
    } catch (err) {
      console.error(err)
    } finally {
      isProcessing.current = false
      setLoading(false)
    }
  }

  const isOwnProfile = currentUserId === targetUserId

  return (
    <div className="flex items-center bg-amber-400/10 border border-amber-400/20 rounded-md overflow-hidden w-max shrink-0">
      {!isOwnProfile && (
        <button
          onClick={() => handleRep(-1)}
          disabled={loading}
          className={`px-2 py-0.5 transition-colors border-r border-amber-400/30 flex items-center justify-center text-[10px] font-black ${
            userVote === -1 
              ? 'bg-rose-500 text-white font-extrabold shadow-inner' 
              : loading 
                ? 'bg-amber-400/10 text-amber-500/50 cursor-not-allowed' 
                : 'bg-amber-400/20 hover:bg-rose-500/30 text-amber-500 hover:text-rose-400'
          }`}
          title="Give -1 Reputation"
        >
          -
        </button>
      )}
      
      <span className="px-2 py-0.5 text-[9px] font-bold text-amber-400 flex items-center gap-1" title="Community Reputation">
        ⭐ {rep} Rep
      </span>
      
      {!isOwnProfile && (
        <button
          onClick={() => handleRep(1)}
          disabled={loading}
          className={`px-2 py-0.5 transition-colors border-l border-amber-400/30 flex items-center justify-center text-[10px] font-black ${
            userVote === 1 
              ? 'bg-emerald-500 text-white font-extrabold shadow-inner' 
              : loading 
                ? 'bg-amber-400/10 text-amber-500/50 cursor-not-allowed' 
                : 'bg-amber-400/20 hover:bg-emerald-500/30 text-amber-500 hover:text-emerald-400'
          }`}
          title="Give +1 Reputation"
        >
          +
        </button>
      )}
    </div>
  )
}